import React, { useState } from 'react'
import Button from '../components/ui/button'
import Loader from '../components/ui/loader.jsx'
import Validator from '../utils/validator'
import apiService from '../services/api.service'
import { useTenant } from '../context/TenantContext'
import { useLanguage } from '../context/LanguageContext'
import { BackIcon } from '../components/icons'

const ForgotPassword = () => {
  const { tenant } = useTenant()
  const { languageId, t } = useLanguage()

  const [formData, setFormData] = useState({ email: '' })
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
    // Clear HTML5 validation message
    e.target.setCustomValidity('')
  }

  // Intercept HTML5 validation and show translated errors
  const handleInvalid = (e) => {
    e.preventDefault()
    const { name, validity } = e.target
    const newErrors = { ...errors }

    if (name === 'email') {
      if (validity.valueMissing) {
        newErrors.email = t('forgotPassword.errors.emailRequired')
      } else if (validity.typeMismatch) {
        newErrors.email = t('forgotPassword.errors.emailInvalid')
      }
    }

    setErrors(newErrors)
    // Set custom validity to prevent browser's default message
    e.target.setCustomValidity(' ')
  }

  const validateForm = () => {
    const newErrors = {}

    // Validate email
    if (!formData.email) {
      newErrors.email = t('forgotPassword.errors.emailRequired')
    } else if (!Validator.isValidEmail(formData.email)) {
      newErrors.email = t('forgotPassword.errors.emailInvalid')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validate form
    if (!validateForm()) {
      return
    }

    setIsLoading(true)

    try {
      await apiService.post('/api/kc/forgot-password', {
        clientemail: formData.email,
        clienttenantid: tenant?.tenantid || 1011,
        defaultlanguageid: languageId || 490,
      })

      // Success - show success state
      setIsSubmitted(true)
    } catch (error) {
      console.error('Forgot password error:', error)

      // Handle API errors - apiService.handleError transforms response to error.data
      const errorMessage = error?.data?.message
      const isAccountLocked =
        errorMessage?.includes('temporarily locked') ||
        errorMessage?.includes('multiple failed attempts')

      // Check for account lockout error
      if (isAccountLocked) {
        // Use translation for account locked message
        setErrors({ submit: t('forgotPassword.errors.accountLocked') })
      } else if (
        error.isRateLimit ||
        error.message === 'Rate limit exceeded. Please try again later.'
      ) {
        setErrors({
          submit:
            t('forgotPassword.errors.rateLimited') ||
            'Too many requests. Please try again later.',
        })
      } else {
        setErrors({
          submit:
            t('forgotPassword.errors.generic') ||
            'Failed to send password reset email. Please try again.',
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      {isLoading && (
        <Loader
          fullscreen
          label={t('forgotPassword.sendingButton')}
          size="xl"
        />
      )}

      <div className="w-full mt-10 md:max-w-[600px] mx-auto">
        <div className="w-full px-6 lg:px-0 md:max-w-[600px] md:mx-auto">
          {/* Forgot Password - START */}
          {!isSubmitted ? (
            <form
              className="form-container"
              onSubmit={handleSubmit}
              aria-label="Forgot Password form"
            >
              <div className="mb-4 text-left">
                <div className="text-center md:!text-left flex items-center mb-5">
                  <button
                    type="button"
                    className="text-blue-900 mr-1 hover:bg-theme-primary/10 rounded-full p-2"
                    onClick={() => window.history.back()}
                  >
                    <BackIcon className="h-5 w-5 text-theme-primary" />
                  </button>
                  <p className="text-base font-bold text-theme-secondary">
                    {t('auth.forgotPassword.title')}
                  </p>
                </div>

                <p className="text-xs text-theme-secondary mt-2">
                  {t('auth.forgotPassword.subtitle')}
                </p>
              </div>

              {/* Display general error */}
              {errors.submit && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600">{errors.submit}</p>
                </div>
              )}

              <div className="space-y-4">
                <div className="grid gap-1">
                  <label
                    htmlFor="email"
                    className="text-sm font-normal text-theme-accent text-left"
                  >
                    {t('auth.forgotPassword.emailLabel')}
                  </label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onInvalid={handleInvalid}
                    required
                    className={`mt-1 w-full h-12 appearance-none rounded-md border ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    } bg-transparent px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:border-theme-primary`}
                    placeholder={t('auth.forgotPassword.emailPlaceholder')}
                    autoComplete="email"
                    aria-required="true"
                    aria-invalid={errors.email ? 'true' : 'false'}
                    aria-describedby={errors.email ? 'email-error' : undefined}
                  />
                  {errors.email && (
                    <div
                      id="email-error"
                      className="text-xs text-red-500 mt-1 text-left flex items-center gap-2"
                    >
                      <span className="w-1 h-1 rounded-full bg-red-500 flex-shrink-0"></span>
                      {errors.email}
                    </div>
                  )}
                </div>

                <Button
                  id="forgot-btn"
                  variant="primary"
                  type="submit"
                  size="lg"
                  disabled={isLoading}
                >
                  {isLoading
                    ? t('forgotPassword.sendingButton')
                    : t('auth.forgotPassword.submitButton')}
                </Button>
              </div>
            </form>
          ) : (
            /* Success State */
            <div className="text-center">
              <div className="mb-6">
                <svg
                  className="mx-auto h-16 w-16 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-theme-secondary mb-2">
                {t('auth.forgotPassword.successTitle')}
              </h2>
              <p className="text-sm text-theme-secondary mb-4">
                {t('auth.forgotPassword.successMessage')
                  .split('{email}')
                  .map((part, index, array) =>
                    index < array.length - 1 ? (
                      <React.Fragment key={index}>
                        {part}
                        <strong>{formData.email}</strong>
                      </React.Fragment>
                    ) : (
                      part
                    ),
                  )}
              </p>
              <p className="text-xs text-gray-500 mb-6">
                {t('auth.forgotPassword.successNote')}
              </p>
              <Button
                variant="secondary"
                onClick={() => {
                  setIsSubmitted(false)
                  setFormData({ email: '' })
                  setErrors({})
                }}
              >
                {t('auth.forgotPassword.sendAnother')}
              </Button>
            </div>
          )}
          {/* Forgot Password - END */}
        </div>
      </div>
    </>
  )
}

export default ForgotPassword
